/**
 * @file: QuotationManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { keyOfIssue } from '@euler/app/flows/task/pages/quotation/functions/utils';
import { isDebugEnabled } from '@euler/functions/debug';
import {
  deleteCachedFile,
  readCachedFileContents,
  writeContentsToCachedFile,
} from '@euler/lib/cache/cacheUtil';
import { CommonTaskStatus, QuotationFormStatus } from '@euler/model/enum';
import {
  CachedTaskDetail,
  QuotationConstructionItemPayload,
  QuotationConstructionItemStaged,
  QuotationFormPayload,
  QuotationFormStaged,
  QuotationIssuePayload,
  QuotationMaterialItemPayload,
  QuotationMaterialItemStaged,
  QuotationSubjectPayload,
  QuotationSubjectStaged,
  TaskDetailVersions,
  TaskQuotation,
  VersionedResponse,
} from '@euler/model/task-detail';
import { makeDebug, onErrorIgnore } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import produce from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { BehaviorSubject, Subscription, debounceTime, skip } from 'rxjs';
import invariant from 'tiny-invariant';
import { TaskWorkflowNodeManager } from '../TaskWorkflowNodeManager';
import { ITaskManager } from '../types';

const debug = makeDebug('taskmanager:quotation', isDebugEnabled('quotation'));

export class QuotationManager extends TaskWorkflowNodeManager {
  readonly staged$: BehaviorSubject<QuotationFormStaged>;
  readonly dirty$: BehaviorSubject<boolean>;

  private changeSubscription?: Subscription;
  private detailSubscription?: Subscription;
  private readonly cacheFilePath: string;
  private isCacheEnabled = false;

  constructor(taskManager: ITaskManager) {
    super(taskManager);

    this.dirty$ = new BehaviorSubject<boolean>(false);

    this.cacheFilePath = this.taskManager.getCacheFilePath('quotation.json');

    this.staged$ = new BehaviorSubject<QuotationFormStaged>({
      version: 0,
      subjects: [],
      timestamp: 0,
    });

    this.detailSubscription = this.taskManager.detail$.subscribe(detail => {
      this.onCachedDetailChange(detail);
    });
  }

  async loadCached(): Promise<QuotationFormStaged | undefined> {
    const contents = await readCachedFileContents(this.cacheFilePath);
    if (contents == null) return undefined;
    return JSON.parse(contents) as QuotationFormStaged;
  }

  applyCached(cached: QuotationFormStaged) {
    this.withChangeObserverTemporarilyDisabled(() => {
      this.staged$.next(cached);
      this.isCacheEnabled = true;
    });
  }

  async discardCache() {
    await deleteCachedFile(this.cacheFilePath, true);
    this.isCacheEnabled = true;
  }

  reset() {
    this.withChangeObserverTemporarilyDisabled(() => {
      const staged = this.toStaged(this.cachedDetail.quotation);
      this.staged$.next(staged);
      this.dirty$.next(false);
    });
  }

  async resetAndClearCache() {
    await this.discardCache();
    this.reset();
  }

  cleanUp() {
    this.stopObserveChanges();

    this.detailSubscription?.unsubscribe();
    this.detailSubscription = undefined;
  }

  dispose() {
    this.cleanUp();
  }

  produce(recipe: (draft: WritableDraft<QuotationFormStaged>) => void) {
    const updated = produce(this.staged$.getValue(), draft => {
      recipe(draft);
    });
    if (updated !== this.staged$.getValue()) {
      this.staged$.next(updated);
      this.dirty$.next(true);
    }
  }

  async save(options?: { confirmed?: boolean }) {
    const payload = this.stagedToPayload();
    const resp = await this.taskService.saveQuotationForm(
      this.taskNo,
      payload,
      options,
    );
    await this.withChangeObserverTemporarilyDisabledAsync(async () => {
      this.handleVersionedResponse(resp, (_, { quotation }) => {
        quotation.quotationForm = resp.response;
        const status =
          resp.response.status === QuotationFormStatus.Confirmed
            ? CommonTaskStatus.Finished
            : CommonTaskStatus.InProgress;
        if (quotation.status !== status) {
          quotation.status = status;
        }
      });
      this.dirty$.next(false);
      await this.discardCache().catch(onErrorIgnore);
    });
  }

  async requestAmend() {
    const formId = this.cachedDetail.basicInfo.quotationFormId;
    invariant(formId != null);
    const resp = await this.taskService.amendQuotationForm(this.taskNo, formId);
    this.handleVersionedResponse(resp, (detail, { quotation, response }) => {
      quotation.status = CommonTaskStatus.InProgress;
      quotation.quotationForm = response;
      detail.basicInfo.quotationStatus = CommonTaskStatus.InProgress;
      detail.basicInfo.settlementStatus = CommonTaskStatus.InProgress;
    });
  }

  startObserveChanges() {
    if (this.changeSubscription) {
      return;
    }

    this.isCacheEnabled = true;

    debug('changes observer started');
    this.changeSubscription = this.staged$
      .pipe(skip(1), debounceTime(500))
      .subscribe(staged => {
        if (!this.isCacheEnabled) return;
        void writeContentsToCachedFile(
          this.cacheFilePath,
          JSON.stringify(
            {
              ...staged,
              timestamp: Date.now(),
            },
            null,
            2,
          ),
        );
        debug('quotation form written to cache');
      });
  }

  stopObserveChanges() {
    this.isCacheEnabled = false;
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
      this.changeSubscription = undefined;
      debug('changes observer stopped');
    }
  }

  private onCachedDetailChange(detail: CachedTaskDetail) {
    if (detail.quotation?.quotationForm == null) {
      return;
    }
    const staged = this.toStaged(detail.quotation);
    if (staged !== this.staged$.getValue()) {
      this.staged$.next(staged);
    }
  }

  private withChangeObserverTemporarilyDisabled<T>(fn: () => T): T {
    this.stopObserveChanges();
    const result = fn();
    this.startObserveChanges();
    return result;
  }

  private async withChangeObserverTemporarilyDisabledAsync<T>(
    fn: () => Promise<T>,
  ): Promise<T> {
    try {
      this.stopObserveChanges();
      const result = await fn();
      return result;
    } finally {
      this.startObserveChanges();
    }
  }

  private stagedToPayload(): QuotationFormPayload {
    const staged = this.staged$.getValue();
    return {
      version: staged.version,
      customerSignImgUrl: staged.customerSignImgUrl,
      customerSignUnavailableReason: staged.customerSignUnavailableReason,
      remark: staged.remark,
      additionalTotalFeeDiscount: integerPrice(
        staged.additionalTotalFeeDiscount,
      ),
      subjects: staged.subjects.map<QuotationSubjectPayload>(subject => ({
        ...subject,
        additionalManHourCostSubTotalDiscount: integerPrice(
          subject.additionalManHourCostSubTotalDiscount,
        ),
        additionalMaterialSubTotalDiscount: integerPrice(
          subject.additionalMaterialSubTotalDiscount,
        ),
        additionalSubTotalDiscount: integerPrice(
          subject.additionalSubTotalDiscount,
        ),
        items: subject.items.map<QuotationConstructionItemPayload>(item => ({
          ...item,
          price: integerPrice(item.price, -1)!,
          discountPrice:
            item.price == null ? undefined : integerPrice(item.discountPrice),
          qty: item.qty ?? 1,
          materials: item.materials.map<QuotationMaterialItemPayload>(
            material => ({
              ...material,
              price: integerPrice(material.price, -1)!,
              discountPrice:
                material.price == null
                  ? undefined
                  : integerPrice(material.discountPrice),
              qty: material.qty ?? 1,
            }),
          ),
        })),
      })),
    };
  }

  private toStaged(quotation: TaskQuotation): QuotationFormStaged {
    const { quotationForm } = quotation;
    return {
      version: quotationForm?.version ?? 0,
      timestamp:
        quotationForm != null
          ? dateFromValue(
              quotationForm?.updatedAt ?? quotationForm?.createdAt,
            ).getTime()
          : 0,
      customerSignImgUrl: quotationForm?.customerSignImgUrl,
      customerSignUnavailableReason:
        quotationForm?.customerSignUnavailableReason,
      remark: quotationForm?.remark,
      additionalTotalFeeDiscount: decimalDiscount(
        quotationForm?.additionalTotalFeeDiscount,
      ),
      subjects:
        quotationForm?.subjects.map<QuotationSubjectStaged>(subject => ({
          ...subject,
          additionalManHourCostSubTotalDiscount: decimalDiscount(
            subject.additionalManHourCostSubTotalDiscount,
          ),
          additionalMaterialSubTotalDiscount: decimalDiscount(
            subject.additionalMaterialSubTotalDiscount,
          ),
          additionalSubTotalDiscount: decimalDiscount(
            subject.additionalSubTotalDiscount,
          ),
          issues: subject.issues.map<QuotationIssuePayload>(issue => ({
            ...issue,
            key: keyOfIssue(issue),
            medias: issue.medias.map(media => ({
              ...media,
              id: undefined,
            })),
          })),
          items:
            subject.items.map<QuotationConstructionItemStaged>(item => ({
              ...item,
              defaultIncluded: item.defaultIncluded,
              price: decimalPrice(item.price),
              discountPrice: decimalPrice(item.discountPrice),
              materials: item.materials.map<QuotationMaterialItemStaged>(
                material => ({
                  ...material,
                  price: decimalPrice(material.price),
                  discountPrice: decimalPrice(material.discountPrice),
                  defaultIncluded: material.defaultIncluded,
                  optionGroup: material.optionGroup,
                }),
              ),
            })) ?? [],
        })) ?? [],
    };
  }

  private handleVersionedResponse<U>(
    versionedResponse: VersionedResponse<U>,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        response: U;
        quotation: TaskQuotation;
        version: number;
        original: TaskQuotation;
      },
    ) => void,
  ) {
    return this.produceWithVersions(
      versionedResponse.versions,
      (draft, context) => {
        recipe(draft, { ...context, response: versionedResponse.response });
      },
    );
  }

  private produceWithVersions(
    versions: TaskDetailVersions,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        quotation: TaskQuotation;
        version: number;
        original: TaskQuotation;
      },
    ) => void,
  ) {
    const original = this.cachedDetail.quotation;
    const version = versions.quotation;

    if (
      version === original.version &&
      this.cachedDetail.version === versions.detail
    ) {
      // nothing changed
      return this.cachedDetail;
    }

    return this.taskManager.produceWithVersions(versions, draft => {
      const { quotation } = draft;
      recipe(draft, {
        quotation,
        version,
        original,
      });
      if (quotation.version !== version) {
        quotation.version = version;
      }
    });
  }
}

const integerPrice = (price: number | undefined, defaultValue?: number) =>
  price == null ? defaultValue : Math.round(price * 100);
const decimalPrice = (price: number | undefined) =>
  price == null || price === -1 ? undefined : price / 100;
const decimalDiscount = (price: number | undefined) =>
  price == null ? undefined : price / 100;
